import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, CheckCircle, Award, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import WorkflowStepCard from '@/components/WorkflowStepCard';
import WorkflowProgress from '@/components/WorkflowProgress';
import ArtifactUpload from '@/components/ArtifactUpload';
import ValidationResults from '@/components/ValidationResults';
import ErrorDisplay from '@/components/ErrorDisplay';
import StandardsExplainer from '@/components/StandardsExplainer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WORKFLOW_STATUS_CONFIG = {
  NOT_STARTED: { color: 'bg-gray-100 text-gray-700', label: 'Not Started' },
  IN_PROGRESS: { color: 'bg-blue-100 text-blue-700', label: 'In Progress' },
  REVIEW: { color: 'bg-amber-100 text-amber-700', label: 'In Review' },
  COMPLETED: { color: 'bg-green-100 text-green-700', label: 'Completed' },
  BLOCKED: { color: 'bg-red-100 text-red-700', label: 'Blocked' }
};

export default function WorkflowDashboard({ user, logout }) {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  
  const [workflow, setWorkflow] = useState(null);
  const [playbook, setPlaybook] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [uploadingForStep, setUploadingForStep] = useState(null);

  useEffect(() => {
    fetchWorkflowStatus();
    // Optional: Poll every 10 seconds if there are long-running jobs
    const interval = setInterval(() => {
      fetchWorkflowStatus(true); // silent refresh
    }, 10000);
    return () => clearInterval(interval);
  }, [workflowId]);

  const fetchWorkflowStatus = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('standari_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/playbooks/workflows/${workflowId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWorkflow(response.data.workflow);
      setPlaybook(response.data.playbook);
      setArtifacts(response.data.artifacts || []);
      setProgress(response.data.progress);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchWorkflowStatus();
  };

  const handleStartStep = async (stepId) => {
    try {
      const token = localStorage.getItem('standari_token');
      await axios.post(
        `${BACKEND_URL}/api/playbooks/workflows/${workflowId}/steps/${stepId}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkflowStatus();
    } catch (err) {
      setError(err);
    }
  };

  const handleUploadArtifact = async (stepId, file) => {
    try {
      const token = localStorage.getItem('standari_token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${BACKEND_URL}/api/playbooks/workflows/${workflowId}/steps/${stepId}/artifacts`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      fetchWorkflowStatus();
      setUploadingForStep(null);
    } catch (err) {
      setError(err);
    }
  };

  const handleCompleteStep = async (stepId) => {
    try {
      const token = localStorage.getItem('standari_token');
      await axios.post(
        `${BACKEND_URL}/api/playbooks/workflows/${workflowId}/steps/${stepId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkflowStatus();
    } catch (err) {
      setError(err);
    }
  };

  const getStepArtifacts = (stepId) => {
    return artifacts.filter(a => a.step_id === stepId);
  };

  const canStartStep = (step) => {
    if (!workflow) return false;
    
    // Check if all dependencies are completed
    for (const depId of step.dependencies || []) {
      if (workflow.step_statuses[depId] !== 'DONE') {
        return false;
      }
    }
    return true;
  };

  const getBlockedReason = (step) => {
    if (!workflow) return null;
    
    for (const depId of step.dependencies || []) {
      if (workflow.step_statuses[depId] !== 'DONE') {
        const depStep = playbook.steps.find(s => s.id === depId);
        return `Complete "${depStep?.name || 'previous step'}" first`;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading workflow...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!workflow || !playbook) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-6xl mx-auto">
          <ErrorDisplay error={error} />
        </div>
      </div>
    );
  }

  const statusConfig = WORKFLOW_STATUS_CONFIG[workflow.status] || WORKFLOW_STATUS_CONFIG.NOT_STARTED;
  const isCompleted = workflow.status === 'COMPLETED';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            onClick={() => navigate('/playbooks')}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Playbooks
          </Button>
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {playbook.name}
              </h1>
              <p className="text-gray-600 mb-3">{playbook.description}</p>
              <div className="flex items-center space-x-3">
                <Badge className={statusConfig.color}>
                  {statusConfig.label}
                </Badge>
                <Badge variant="outline">
                  Workflow ID: {workflow.id.slice(0, 8)}
                </Badge>
              </div>
            </div>
            <Button
              onClick={handleRefresh}
              variant="outline"
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6">
            <ErrorDisplay error={error} onRetry={fetchWorkflowStatus} />
          </div>
        )}

        {/* Completion Banner */}
        {isCompleted && (
          <Card className="mb-6 border-2 border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-green-600 rounded-full">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-green-900 mb-1">
                    Workflow Complete!
                  </h3>
                  <p className="text-green-700">
                    All steps have been completed successfully. Your compliant documentation is ready.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        {progress && (
          <div className="mb-6">
            <WorkflowProgress
              totalSteps={progress.total_steps}
              completedSteps={progress.completed_steps}
              currentStepOrder={progress.current_step_order}
            />
          </div>
        )}

        {/* Standards Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Award className="h-5 w-5 mr-2" />
              Applied Standards
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StandardsExplainer selectedStandards={playbook.standards_required} />
          </CardContent>
        </Card>

        {/* Steps */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Workflow Steps</h2>
          
          {playbook.steps.map((step, index) => {
            const stepStatus = workflow.step_statuses[step.id] || 'NOT_STARTED';
            const stepArtifacts = getStepArtifacts(step.id);
            const canStart = canStartStep(step);
            const blockedReason = getBlockedReason(step);

            return (
              <div key={step.id}>
                <WorkflowStepCard
                  step={step}
                  stepNumber={index + 1}
                  status={stepStatus}
                  canStart={canStart}
                  blockedReason={blockedReason}
                  onStart={() => handleStartStep(step.id)}
                  onUploadArtifact={(file) => handleUploadArtifact(step.id, file)}
                  onComplete={() => handleCompleteStep(step.id)}
                  artifacts={stepArtifacts}
                />

                {/* Artifact Upload (when triggered) */}
                {uploadingForStep === step.id && (
                  <div className="mt-4">
                    <ArtifactUpload
                      workflowId={workflowId}
                      stepId={step.id}
                      stepName={step.name}
                      onUploadSuccess={() => {
                        setUploadingForStep(null);
                        fetchWorkflowStatus();
                      }}
                      onClose={() => setUploadingForStep(null)}
                    />
                  </div>
                )}

                {/* Validation Results (if any artifacts) */}
                {stepArtifacts.length > 0 && stepArtifacts[0].validation && (
                  <div className="mt-4">
                    <ValidationResults
                      validation={stepArtifacts[0].validation}
                      stepName={step.name}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
